import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertCircle, User, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { GhostProtocolModal } from '../components/GhostProtocolModal';
import { RhinoOneBadge } from '../components/RhinoOneBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState({
    logo_url: '/rsj-logo.svg',
    login_title: 'Welcome Back',
    login_subtitle: 'Enter your credentials to access your dashboard',
    login_background_url: 'https://customer-assets.emergentagent.com/job_rs-workspace/artifacts/ltngn5pv_H11.jpg'
  });
  
  // Secret admin feature
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminPasswordDialogOpen, setAdminPasswordDialogOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Master password for Ghost Protocol access
  const GHOST_MASTER_PASSWORD = '050378';
  
  // Forgot password feature
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotName, setForgotName] = useState('');
  const [forgotPin, setForgotPin] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  
  // Help dialog
  const [helpOpen, setHelpOpen] = useState(false);
  
  // Ghost Protocol for login
  const [ghostProtocolOpen, setGhostProtocolOpen] = useState(false);
  const [pendingLoginUser, setPendingLoginUser] = useState(null);
  const [pendingLoginRedirect, setPendingLoginRedirect] = useState('/dashboard');
  
  // Ghost Protocol enabled setting from admin
  const [ghostProtocolEnabled, setGhostProtocolEnabled] = useState(false);
  
  // Super Admin Shortcut setting
  const [superAdminShortcutEnabled, setSuperAdminShortcutEnabled] = useState(true);
  const [signInClickCount, setSignInClickCount] = useState(0);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/branding`);
        setBranding(response.data);
      } catch (error) {
        // Use default branding if fetch fails
        console.error('Failed to load branding:', error);
      }
    };
    
    const fetchLoginShortcuts = async () => {
      try {
        const response = await axios.get(`${API}/settings/security/login-shortcuts`);
        setGhostProtocolEnabled(response.data.ghost_protocol_enabled);
        setSuperAdminShortcutEnabled(response.data.super_admin_shortcut_enabled);
      } catch (error) {
        // Default values if fetch fails
        console.error('Failed to load login shortcuts:', error);
        setGhostProtocolEnabled(false);
        setSuperAdminShortcutEnabled(true);
      }
    };
    
    fetchBranding();
    fetchLoginShortcuts();
  }, []);

  // Progressive Release Lockdown - must match Layout.js
  const PROGRESSIVE_RELEASE_LOCKDOWN = true;

  const getRedirectPath = (role) => {
    // During lockdown, all non-admin users go to the basic dashboard
    if (PROGRESSIVE_RELEASE_LOCKDOWN && role !== 'super_admin' && role !== 'admin') {
      return '/dashboard';
    }
    
    if (role === 'ceo' || role === 'president' || role === 'president_&_ceo') {
      return '/ceo/dashboard';
    } else if (role === 'super_admin' || role === 'admin') {
      return '/admin';
    } else if (role === 'director_of_sales') {
      return '/director/dashboard';
    } else if (role === 'general_manager') {
      return '/gm/dashboard';
    } else if (role === 'sales' || role === 'sales_manager') {
      return '/dashboard';
    } else if (role === 'call_center' || role === 'call_center_manager') {
      return '/dashboard';
    } else if (role === 'operations_manager') {
      return '/dashboard';
    } else {
      return '/dashboard';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, rememberMe);
      
      if (result.success) {
        const role = result.user?.role;
        const redirectPath = getRedirectPath(role);
        
        console.log('=== LOGIN DEBUG ===');
        console.log('User role:', role);
        console.log('User object:', result.user);
        
        // Check if user has ghost login protection enabled
        if (result.user?.ghost_login_protected && result.user?.ghost_login_password) {
          // Show ghost protocol modal before allowing access
          setPendingLoginUser(result.user);
          setPendingLoginRedirect(redirectPath);
          setGhostProtocolOpen(true);
          setLoading(false);
          return;
        }
        
        toast.success('Login successful!');
        navigate(redirectPath);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGhostProtocolSuccess = () => {
    setGhostProtocolOpen(false);
    toast.success('Login successful!');
    navigate(pendingLoginRedirect);
    setPendingLoginUser(null);
  };

  // Super Admin Shortcut - 3 clicks on Sign In button within 600ms window
  const clickTimeoutRef = React.useRef(null);
  const clickCountRef = React.useRef(0);
  const lastClickTimeRef = React.useRef(0);
  
  const handleSignInClick = async (e) => {
    // Always prevent default - we'll handle form submission manually
    e.preventDefault();
    e.stopPropagation();
    
    // Check if shortcut is enabled
    if (!superAdminShortcutEnabled) {
      // Shortcut disabled - just submit the form normally
      handleSubmit(e);
      return;
    }
    
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // Reset counter if more than 600ms since last click
    if (timeSinceLastClick > 600) {
      clickCountRef.current = 0;
    }
    
    clickCountRef.current += 1;
    lastClickTimeRef.current = now;
    
    // Clear any existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    if (clickCountRef.current >= 3) {
      // Reset counter immediately
      clickCountRef.current = 0;
      
      // Triple-click shortcut disabled - use normal login
      toast.info('Please enter your credentials to log in.');
      return;
    }
    
    // Set timeout - if no more clicks within 600ms, submit form normally
    clickTimeoutRef.current = setTimeout(() => {
      if (clickCountRef.current > 0 && clickCountRef.current < 3) {
        clickCountRef.current = 0;
        handleSubmit(e);
      }
    }, 600);
  };

  const handleGhostProtocolCancel = () => {
    setGhostProtocolOpen(false);
    setPendingLoginUser(null);
    // Log out the user since they can't verify ghost protocol
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setError('Ghost Protocol verification cancelled. Please try again.');
  };

  // Forgot password handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    
    // Validate PIN
    if (forgotPin !== '1234') {
      setForgotError('Invalid PIN. Please enter the correct PIN.');
      return;
    }
    
    if (!forgotEmail || !forgotName) {
      setForgotError('Please enter your name and email.');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      // Find user by email and name match
      const usersRes = await axios.get(`${API}/users`);
      const users = usersRes.data;
      
      const matchedUser = users.find(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const emailMatch = user.email?.toLowerCase() === forgotEmail.toLowerCase();
        const nameMatch = fullName.includes(forgotName.toLowerCase()) || 
                          forgotName.toLowerCase().includes(user.first_name?.toLowerCase()) ||
                          forgotName.toLowerCase().includes(user.last_name?.toLowerCase());
        return emailMatch && nameMatch;
      });
      
      if (!matchedUser) {
        setForgotError('No matching account found. Please check your name and email.');
        setForgotLoading(false);
        return;
      }
      
      // Log in as the matched user using impersonation
      const impersonateRes = await axios.post(`${API}/auth/impersonate`, {
        user_id: matchedUser.id
      });
      
      if (impersonateRes.data.access_token) {
        localStorage.setItem('token', impersonateRes.data.access_token);
        localStorage.setItem('user', JSON.stringify(impersonateRes.data.user));
        
        toast.success(`Welcome back, ${matchedUser.first_name}!`);
        setForgotPasswordOpen(false);
        
        // Navigate based on role
        const role = impersonateRes.data.user.role;
        if (role === 'ceo' || role === 'super_admin') {
          navigate('/ceo/dashboard');
        } else {
          navigate('/dashboard');
        }
        
        window.location.reload();
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotError('Unable to log in. Please try again or contact support.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Secret admin feature - logo click handler
  const handleLogoClick = () => {
    // Check if Ghost Protocol is enabled in admin settings
    if (!ghostProtocolEnabled) {
      // Ghost Protocol is disabled, don't show the dialog
      return;
    }
    
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    if (newCount === 3) {
      // Reset counter and open admin PASSWORD dialog first
      setLogoClickCount(0);
      setAdminPassword('');
      setAdminPasswordError('');
      setAdminPasswordDialogOpen(true);
    }
    
    // Reset counter after 2 seconds if not completed
    setTimeout(() => {
      setLogoClickCount(0);
    }, 2000);
  };

  // State to track who authenticated with Ghost Protocol
  const [ghostProtocolUser, setGhostProtocolUser] = useState(null);

  // Handle admin password submission - check master password OR user pins
  const handleAdminPasswordSubmit = async (e) => {
    e.preventDefault();
    
    // First check if it's the master password (Ryan March - Super Admin)
    if (adminPassword === GHOST_MASTER_PASSWORD) {
      setAdminPasswordDialogOpen(false);
      setAdminPassword('');
      setAdminPasswordError('');
      setGhostProtocolUser({ email: 'Rmarch@rhinoshield.com', isSuperAdmin: true });
      // Now fetch users and open the user list
      fetchAllUsers();
      setAdminDialogOpen(true);
      return;
    }
    
    // Otherwise check if any user has this pin
    try {
      const res = await axios.get(`${API}/users`);
      const users = res.data;
      const userWithPin = users.find(u => 
        u.ghost_protocol_access && 
        u.ghost_protocol_pin === adminPassword
      );
      
      if (userWithPin) {
        setAdminPasswordDialogOpen(false);
        setAdminPassword('');
        setAdminPasswordError('');
        setGhostProtocolUser({ 
          email: userWithPin.email, 
          isSuperAdmin: false,
          userId: userWithPin.id,
          firstName: userWithPin.first_name,
          lastName: userWithPin.last_name
        });
        // Store this user as the "admin" for return purposes
        localStorage.setItem('ghost_protocol_user', JSON.stringify({
          email: userWithPin.email,
          id: userWithPin.id,
          first_name: userWithPin.first_name,
          last_name: userWithPin.last_name
        }));
        // Fetch users and open the user list
        fetchAllUsers();
        setAdminDialogOpen(true);
      } else {
        setAdminPasswordError('Invalid pin. Access denied.');
      }
    } catch (error) {
      console.error('Error checking pin:', error);
      setAdminPasswordError('Invalid pin. Access denied.');
    }
  };

  const fetchAllUsers = async () => {
    try {
      // Try to get admin token (check if we're currently impersonating)
      let token = localStorage.getItem('admin_original_token') || 
                  localStorage.getItem('token') || 
                  localStorage.getItem('access_token');
      
      // If no token, the user needs to login first - don't use hardcoded credentials
      if (!token) {
        console.log('No token found - user must be logged in to use Ghost Protocol');
        toast.error('Please log in first to use Ghost Protocol.');
        return;
      }
      
      console.log('Fetching users with token...');
      const usersRes = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`Successfully loaded ${usersRes.data.length} users`);
      setAllUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // If it's a 401, the token is invalid - user needs to re-login
      if (error.response?.status === 401) {
        console.log('Token invalid - user needs to re-login');
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      toast.error('Failed to load users. Please try logging in as admin first.');
    }
  };

  // State for ghost protocol on impersonation - no longer needed since password is checked upfront
  const [pendingImpersonateUser, setPendingImpersonateUser] = useState(null);
  const [impersonateGhostModalOpen, setImpersonateGhostModalOpen] = useState(false);

  const impersonateUser = async (user) => {
    // Password already verified at Ghost Protocol entry
    // Directly proceed with impersonation for any user
    await proceedWithImpersonation(user);
  };

  const proceedWithImpersonation = async (user) => {
    try {
      // Get current admin token (prefer stored admin token)
      let adminToken = localStorage.getItem('admin_original_token') || 
                       localStorage.getItem('token') || 
                       localStorage.getItem('access_token');
      
      // If no token, can't proceed with impersonation
      if (!adminToken) {
        console.log('No admin token available for impersonation');
        toast.error('Please log in as admin first to use impersonation.');
        return;
      }
      
      // Store original admin token and email before impersonating (if not already stored)
      if (!localStorage.getItem('admin_original_token')) {
        localStorage.setItem('admin_original_token', adminToken);
      }
      
      // Check if user is selecting their own profile
      const isSelectingOwnProfile = ghostProtocolUser?.email === user.email;
      const isRyanMarchSelecting = ghostProtocolUser?.isSuperAdmin;
      const isRyanMarch = user.email?.toLowerCase() === 'rmarch@rhinoshield.com';
      
      // Determine if we should show impersonation banner
      if (isSelectingOwnProfile) {
        // User selecting their own profile - no banner
        localStorage.removeItem('is_impersonating');
        localStorage.removeItem('impersonating_as');
        localStorage.removeItem('ghost_protocol_user');
      } else if (isRyanMarchSelecting && isRyanMarch) {
        // Ryan March selecting himself - no banner
        localStorage.removeItem('is_impersonating');
        localStorage.removeItem('impersonating_as');
      } else {
        // Someone viewing another profile - show banner
        localStorage.setItem('is_impersonating', 'true');
        localStorage.setItem('impersonating_as', user.email);
        
        // Store who is doing the viewing (for "Return to your profile")
        if (!isRyanMarchSelecting && ghostProtocolUser) {
          localStorage.setItem('ghost_protocol_user', JSON.stringify(ghostProtocolUser));
        }
      }
      
      console.log(`Requesting impersonation for user: ${user.email}`);
      
      // Request impersonation token
      const impersonateRes = await axios.post(
        `${API}/auth/impersonate`,
        { user_id: user.id },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      const impersonateToken = impersonateRes.data.access_token;
      const impersonateRefreshToken = impersonateRes.data.refresh_token;
      
      // Store token and user data using AuthContext keys
      localStorage.setItem('access_token', impersonateToken);
      localStorage.setItem('refresh_token', impersonateRefreshToken);
      
      // Clear any "view as" settings
      localStorage.removeItem('viewAsRole');
      localStorage.removeItem('viewAsUser');
      
      setAdminDialogOpen(false);
      toast.success(`Logged in as ${user.first_name} ${user.last_name} (${user.role})`);
      
      // Role-based routing
      const role = user.role;
      let targetPath = '/dashboard';
      
      if (role === 'ceo' || role === 'president' || role === 'president_&_ceo') {
        targetPath = '/ceo/dashboard';
      } else if (role === 'super_admin' || role === 'admin') {
        targetPath = '/admin';
      } else if (role === 'director_of_sales') {
        targetPath = '/director/dashboard';
      } else if (role === 'general_manager') {
        targetPath = '/gm/dashboard';
      }
      
      console.log('=== IMPERSONATION DEBUG ===');
      console.log('Impersonating role:', role);
      console.log('Target path:', targetPath);
      
      // Store the target path before reload
      sessionStorage.setItem('impersonate_redirect', targetPath);
      
      // Reload to apply new auth context
      window.location.reload();
    } catch (error) {
      console.error('Error impersonating user:', error);
      toast.error('Failed to impersonate user');
    }
  };

  // Leadership names (case-insensitive matching)
  const leadershipNames = [
    'ryan adams',
    'gabriel gecan',
    'ryan march',
    'lauren marketing',
    'stephen creech',
    'trevor knowles'
  ];

  // Helper to check if user is in leadership
  const isLeadership = (user) => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return leadershipNames.some(name => fullName.includes(name) || name.includes(fullName));
  };

  // Map roles to department names for grouping
  const getRoleDepartment = (role) => {
    const roleMap = {
      'ceo': 'Executive',
      'president': 'Executive',
      'president_&_ceo': 'Executive',
      'super_admin': 'Administration',
      'admin': 'Administration',
      'general_manager': 'Management',
      'director_of_sales': 'Sales',
      'sales_manager': 'Sales',
      'sales': 'Sales',
      'call_center': 'Call Center',
      'call_center_agent': 'Call Center',
      'call_center_manager': 'Call Center',
      'operations_manager': 'Operations',
      'production_manager': 'Production',
      'production': 'Production',
      'crew_lead': 'Production',
      'project_manager': 'Operations',
      'marketing': 'Marketing'
    };
    return roleMap[role?.toLowerCase()] || 'Other';
  };

  // Format role for display
  const formatRole = (role) => {
    if (!role) return 'N/A';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Filter users based on search AND hide Ryan March from non-super-admin users
  const filteredUsers = allUsers.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase());
    
    // If the user viewing is NOT super admin (Ryan March), hide Ryan March from the list
    const isRyanMarchProfile = user.email?.toLowerCase() === 'rmarch@rhinoshield.com';
    const viewerIsSuperAdmin = ghostProtocolUser?.isSuperAdmin;
    
    // Only show Ryan March if the viewer is Ryan March himself
    if (isRyanMarchProfile && !viewerIsSuperAdmin) {
      return false;
    }
    
    return matchesSearch;
  });

  // Organize users: Leadership first, then by department
  const organizedUsers = () => {
    const leadership = [];
    const departments = {};

    filteredUsers.forEach(user => {
      if (isLeadership(user)) {
        leadership.push(user);
      } else {
        const dept = getRoleDepartment(user.role);
        if (!departments[dept]) departments[dept] = [];
        departments[dept].push(user);
      }
    });

    // Sort leadership by the predefined order
    leadership.sort((a, b) => {
      const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
      const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
      const aIndex = leadershipNames.findIndex(name => aName.includes(name) || name.includes(aName));
      const bIndex = leadershipNames.findIndex(name => bName.includes(name) || name.includes(bName));
      return aIndex - bIndex;
    });

    // Define department order
    const deptOrder = ['Executive', 'Management', 'Administration', 'Sales', 'Call Center', 'Operations', 'Production', 'Marketing', 'Other'];
    const sortedDepts = Object.keys(departments).sort((a, b) => {
      return deptOrder.indexOf(a) - deptOrder.indexOf(b);
    });

    return { leadership, departments, sortedDepts };
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: branding.login_background_url ? `url(${branding.login_background_url})` : 'linear-gradient(to bottom right, #f8fafc, #e0f2fe, #f8fafc)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay for better readability - only show if background image exists */}
      {branding.login_background_url && (
        <div className="absolute inset-0 bg-black/30 dark:bg-black/50"></div>
      )}
      
      {/* Main Container - Combined Rhino + Login Box */}
      <div 
        className="relative z-10 flex flex-col items-center"
      >
        <div 
          className="flex rounded-2xl overflow-hidden border-2 border-blue-500 bg-white"
          style={{
            boxShadow: '0 0 30px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2), 0 0 100px rgba(59, 130, 246, 0.1)'
          }}
        >
          {/* Left Side - RHINO-1 Badge - THE STATEMENT */}
          <div className="hidden lg:flex flex-col items-center justify-end overflow-hidden bg-gradient-to-b from-blue-900 via-blue-800 to-slate-900" style={{ width: '480px', minHeight: '520px' }}>
            {/* Giant Badge - Fills the entire space above text */}
            <div className="flex-1 flex items-center justify-center w-full p-0 overflow-hidden">
              <img 
                src="/assets/branding/rhino-1-badge-transparent.png"
                alt="RHINO-1 Platform Badge"
                className="object-contain"
                style={{ 
                  transform: 'scale(1.87)',
                  filter: 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.2))'
                }}
              />
            </div>
            
            {/* Text pinned to bottom */}
            <div className="text-center py-5 w-full bg-slate-900/50">
              <h2 className="text-3xl font-bold text-white mb-1 tracking-tight">RHINO-1 Platform</h2>
              <p className="text-blue-300 text-xs tracking-[0.3em] uppercase font-medium">Enterprise Operations System</p>
            </div>
          </div>

        {/* Right Side - Login Form */}
        <div className="w-full max-w-md p-8">
          {/* Logo - Larger */}
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <img 
                src={branding.logo_url} 
                alt="Platform Logo" 
                className="h-32 w-auto cursor-pointer"
                onClick={handleLogoClick}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <h1 
                className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent hidden"
              >
                {branding.company_name || 'Platform'}
              </h1>
            </div>
          </div>

          {/* Login Form */}
          <div data-testid="login-card">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1">
                <p className="text-slate-700 text-sm font-medium">Enter your RhinoShield credentials to login.</p>
                <Label htmlFor="email" className="text-slate-900 font-semibold">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@rhinoshieldflorida.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  data-testid="email-input"
                  className="h-11 bg-white caret-slate-900 text-slate-900"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-900 font-semibold">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  data-testid="password-input"
                  className="h-11 bg-white caret-slate-900 text-slate-900"
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    disabled={loading}
                    data-testid="remember-me-checkbox"
                  />
                  <Label
                    htmlFor="remember"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Remember me
                  </Label>
                </div>
                <button
                  type="button"
                  onClick={() => setForgotPasswordOpen(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="button"
                className="w-full h-11 text-base"
                disabled={loading}
                data-testid="login-submit-button"
                onClick={handleSignInClick}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
              <p>Use your RhinoShield Florida email to access the platform</p>
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="text-blue-600 hover:text-blue-800"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </div>
            
            {/* Customer Portal Link */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <p className="text-center text-sm text-slate-600">
                Are you a customer?{' '}
                <a 
                  href="/customer-login" 
                  className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                >
                  Access Customer Portal →
                </a>
              </p>
            </div>
          </div>
        </div>
        </div>
        
        {/* Footer - Under login box */}
        <p className="text-center text-sm font-bold text-white mt-6 drop-shadow-lg">
          &copy; 2025 RhinoShield. <span className="text-blue-300">POWERED by RHINO-1</span> All rights reserved.
        </p>
      </div>

      {/* Ghost Protocol Dialog */}
      {/* Admin Password Dialog - First step of Ghost Protocol */}
      <Dialog open={adminPasswordDialogOpen} onOpenChange={setAdminPasswordDialogOpen}>
        <DialogContent 
          className="max-w-sm bg-slate-900 border-2 border-amber-500 text-white"
          style={{
            boxShadow: '0 0 40px rgba(245, 158, 11, 0.4), 0 0 80px rgba(245, 158, 11, 0.2)'
          }}
        >
          <DialogHeader className="text-center">
            <div className="flex flex-col items-center gap-3">
              <div 
                className="h-16 w-16 rounded-full bg-amber-500/20 border-2 border-amber-500 flex items-center justify-center"
                style={{ boxShadow: '0 0 20px rgba(245, 158, 11, 0.4)' }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_data-dashboard-hub/artifacts/go2osn0g_Painter%20Rhino%20in%20Blue%20T-shirt.png" 
                  alt="Rhino" 
                  className="h-10 w-10 object-contain"
                />
              </div>
              <DialogTitle className="text-xl font-bold text-amber-400">
                Ghost Protocol Access
              </DialogTitle>
              <p className="text-sm text-slate-400">
                Enter admin password to access user impersonation
              </p>
            </div>
          </DialogHeader>
          
          <form onSubmit={handleAdminPasswordSubmit} className="space-y-4 pt-4">
            <div>
              <Input
                type="password"
                placeholder="Enter admin password"
                value={adminPassword}
                onChange={(e) => {
                  setAdminPassword(e.target.value);
                  setAdminPasswordError('');
                }}
                className="bg-slate-800 border-amber-500/50 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                autoFocus
              />
              {adminPasswordError && (
                <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {adminPasswordError}
                </p>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setAdminPasswordDialogOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                Access Ghost Protocol
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin User List Dialog - Shows after password is verified */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh] overflow-hidden bg-slate-900 border-2 border-blue-500 text-white"
          style={{
            boxShadow: '0 0 30px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2), 0 0 100px rgba(59, 130, 246, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-400 flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_data-dashboard-hub/artifacts/go2osn0g_Painter%20Rhino%20in%20Blue%20T-shirt.png" 
                alt="Rhino" 
                className="h-8 w-8 object-contain"
              />
              Ghost Protocol
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input
              placeholder="Search users by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20"
            />
            
            <div className="max-h-96 overflow-y-auto space-y-4 pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
              {filteredUsers.length > 0 ? (
                <>
                  {/* Leadership Section */}
                  {organizedUsers().leadership.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2 sticky top-0 bg-slate-900 py-1">
                        <span className="text-yellow-400 text-lg">👑</span>
                        <h3 className="text-sm font-bold text-yellow-400 uppercase tracking-wider">Leadership</h3>
                        <div className="flex-1 h-px bg-yellow-400/30"></div>
                      </div>
                      <div className="space-y-2">
                        {organizedUsers().leadership.map(user => (
                          <button
                            key={user.id}
                            onClick={() => impersonateUser(user)}
                            className="w-full p-3 text-left border border-yellow-500/30 rounded-lg bg-yellow-500/5 hover:bg-yellow-500/10 hover:border-yellow-500/50 transition-all duration-200 flex items-center gap-3 group"
                          >
                            <div className="h-10 w-10 rounded-full bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center group-hover:bg-yellow-500/30 transition-colors font-bold text-yellow-400">
                              {user.employee_number || <User className="h-5 w-5 text-yellow-400" />}
                            </div>
                            <div className="flex-1 flex items-center justify-between">
                              <div className="font-semibold text-white group-hover:text-yellow-300 transition-colors">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-yellow-400/80 bg-yellow-500/10 px-2 py-1 rounded">
                                {formatRole(user.role)}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Department Sections */}
                  {organizedUsers().sortedDepts.map(dept => (
                    <div key={dept}>
                      <div className="flex items-center gap-2 mb-2 sticky top-0 bg-slate-900 py-1">
                        <span className="text-blue-400 text-lg">
                          {dept === 'Sales' && '💼'}
                          {dept === 'Call Center' && '📞'}
                          {dept === 'Operations' && '⚙️'}
                          {dept === 'Production' && '🔧'}
                          {dept === 'Marketing' && '📣'}
                          {dept === 'Administration' && '🛡️'}
                          {dept === 'Executive' && '🏢'}
                          {dept === 'Management' && '📊'}
                          {dept === 'Other' && '👤'}
                        </span>
                        <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider">{dept}</h3>
                        <div className="flex-1 h-px bg-blue-400/30"></div>
                      </div>
                      <div className="space-y-2">
                        {organizedUsers().departments[dept].map(user => (
                          <button
                            key={user.id}
                            onClick={() => impersonateUser(user)}
                            className="w-full p-3 text-left border border-slate-700 rounded-lg bg-slate-800/50 hover:bg-slate-700 hover:border-blue-500/50 transition-all duration-200 flex items-center gap-3 group"
                          >
                            <div className="h-10 w-10 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors text-sm font-mono text-blue-400">
                              {user.employee_number || <User className="h-5 w-5 text-blue-400" />}
                            </div>
                            <div className="flex-1 flex items-center justify-between">
                              <div className="font-semibold text-white group-hover:text-blue-300 transition-colors">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-blue-400/80 bg-blue-500/10 px-2 py-1 rounded">
                                {formatRole(user.role)}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  {allUsers.length === 0 ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span>Loading users...</span>
                    </div>
                  ) : 'No users found'}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent 
          className="max-w-md bg-slate-900 border-2 border-blue-500 text-white"
          style={{
            boxShadow: '0 0 30px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-400">
              🔑 Forgot Password
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <p className="text-slate-300 text-sm">
              Enter your details below to access your account without a password.
            </p>
            
            {forgotError && (
              <Alert variant="destructive" className="bg-red-900/50 border-red-500">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{forgotError}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label className="text-slate-200">Your Name</Label>
              <Input
                placeholder="Enter your first or full name"
                value={forgotName}
                onChange={(e) => setForgotName(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-200">RhinoShield Email</Label>
              <Input
                type="email"
                placeholder="your.email@rhinoshieldflorida.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-200">Recovery PIN</Label>
              <Input
                type="text"
                placeholder="Enter 4-digit PIN"
                value={forgotPin}
                onChange={(e) => setForgotPin(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
                maxLength={4}
                required
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotPasswordOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={forgotLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {forgotLoading ? 'Verifying...' : 'Login'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent 
          className="max-w-md bg-slate-900 border-2 border-blue-500 text-white"
          style={{
            boxShadow: '0 0 30px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2)'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-400">
              ❓ Login Help
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 text-slate-300">
            <div>
              <h3 className="font-semibold text-white mb-2">How to Login</h3>
              <p className="text-sm">
                Enter your RhinoShield Florida email address and password, then click "Sign In".
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">Trouble Logging In?</h3>
              <p className="text-sm">
                If you've forgotten your password, click <span className="text-blue-400 font-medium">"Forgot Password?"</span> to access your account using:
              </p>
              <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                <li>Your name</li>
                <li>Your RhinoShield email</li>
                <li>Recovery PIN: <span className="text-blue-400 font-mono">1234</span></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">Still Need Help?</h3>
              <p className="text-sm">
                Contact your administrator or IT support for assistance.
              </p>
            </div>
            
            <Button
              onClick={() => setHelpOpen(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 mt-2"
            >
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ghost Protocol Modal for Login */}
      <GhostProtocolModal
        isOpen={ghostProtocolOpen}
        onClose={handleGhostProtocolCancel}
        onSuccess={handleGhostProtocolSuccess}
        userName={pendingLoginUser ? `${pendingLoginUser.first_name} ${pendingLoginUser.last_name}` : 'User'}
        requiredPassword={pendingLoginUser?.ghost_login_password || ''}
        title="Ghost Protocol Protection"
        description="This account is protected with Ghost Protocol. Please enter your security password to continue."
      />
    </div>
  );
};